import React, { useCallback, useEffect, useMemo, useState } from "react";
import TeamMemberCard from "./TeamMemberCard";
import { buildApiUrl } from "../utils/apiConfig";
import { jsonGet } from "../utils/http";
import { getTermsAccepted, getUserRole, isCleverStaffOrAdministrator, isCoach } from "../utils/auth";
import "../styles/team-members.css";
import "../styles/team-page.css";
import AddTeamMemberButton from "./AddTeamMemberButton";
import AddTeamMemberModal from "./AddTeamMemberModal";
import { Profile } from "./AddTeamMemberProfileForm";
import { useAuth } from "../contexts/AuthContext";

type ApiMember = {
    id?: number;
    adminId?: number;
    name: string;
    email?: string;
    avatarUrl?: string;
    imageUrl?: string;
    logo?: boolean;
    viewTeamHref?: string;
    roleName: string;
};

type ApiResponse = {
    companyMemberCards?: ApiMember[];
    teamName?: string;
};

export type TeamRoleSectionId = "admins" | "staff" | "coaches";

export const TEAM_ROLE_SECTION_ORDER: Array<{ id: TeamRoleSectionId; title: string }> = [
    { id: "admins", title: "Admin" },
    { id: "staff", title: "Staff" },
    { id: "coaches", title: "Coach" },
];

export function resolveTeamRoleSection(roleName?: string | null): TeamRoleSectionId | null {
    const key = (roleName ?? "").trim().toLowerCase();
    if (!key) {
        return null;
    }
    if (key.includes("admin")) {
        return "admins";
    }
    if (key === "staff") {
        return "staff";
    }
    if (key === "coach") {
        return "coaches";
    }
    return null;
}

export function groupTeamMembersByRole<T extends { roleName?: string }>(members: T[]): {
    sections: Array<{ id: TeamRoleSectionId; title: string; members: T[] }>;
    unmatched: T[];
} {
    const buckets: Record<TeamRoleSectionId, T[]> = {
        admins: [],
        staff: [],
        coaches: [],
    };
    const unmatched: T[] = [];

    for (const member of members) {
        const section = resolveTeamRoleSection(member.roleName);
        if (section) {
            buckets[section].push(member);
        } else {
            unmatched.push(member);
        }
    }

    return {
        sections: TEAM_ROLE_SECTION_ORDER
            .map((section) => ({ ...section, members: buckets[section.id] }))
            .filter((section) => section.members.length > 0),
        unmatched,
    };
}

function memberCardKey(member: ApiMember): string {
    if (member.id != null) {
        return `id-${member.id}`;
    }
    if (member.email) {
        return `email-${member.email}`;
    }
    return `name-${member.name}`;
}

function memberUserId(member: ApiMember): number {
    const userIdFromHref = member.viewTeamHref?.split("/").pop();
    return member.id ?? member.adminId ?? (userIdFromHref ? parseInt(userIdFromHref, 10) : 0);
}

function TeamMemberCards({ members }: { members: ApiMember[] }) {
    return (
        <div className="tm-grid">
            {members.map((member) => (
                <TeamMemberCard
                    key={memberCardKey(member)}
                    userId={memberUserId(member)}
                    href={member.viewTeamHref}
                    name={member.name}
                    email={member.email}
                    imageUrl={member.imageUrl ?? member.avatarUrl}
                    logo={!!member.logo}
                    roleLabel={member.roleName}
                />
            ))}
        </div>
    );
}

export default function TeamMemberGrid() {
    const [members, setMembers] = useState<ApiMember[]>([]);
    const [teamName, setTeamName] = useState<string | undefined>(undefined);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [activeTab, setActiveTab] = useState<TeamRoleSectionId | null>(null);
    const { authProvider } = useAuth();
    const userRole = getUserRole();
    const showAddTeamMember = useMemo(
        () => !isCoach() && (authProvider !== "clever" || isCleverStaffOrAdministrator(authProvider, userRole)),
        [authProvider, userRole]
    );

    const loadMembers = useCallback(async (opts?: { silent?: boolean }) => {
        const hasAcceptedTerms = getTermsAccepted();
        if (!hasAcceptedTerms) {
            setLoading(false);
            return;
        }

        const silent = opts?.silent ?? false;
        if (!silent) {
            setLoading(true);
        }
        setError(null);
        try {
            const res = await jsonGet<ApiResponse>(
                `${buildApiUrl("api/company/members")}?_t=${Date.now()}`,
                true
            );
            setMembers(res?.companyMemberCards ?? []);
            setTeamName(res?.teamName);
        } catch (e: any) {
            const errorMsg = e?.message || "Failed to load team members.";
            setError(errorMsg);

            if (errorMsg.includes("Unable to find") && errorMsg.includes("User with id")) {
                console.warn("User ID in localStorage is invalid. Clearing auth data.");
                localStorage.removeItem("loggedInUserId");
                localStorage.removeItem("jwt");
                localStorage.removeItem("authProvider");
                setError("Your session has expired. Please log in again.");
            }
        } finally {
            if (!silent) {
                setLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        void loadMembers();
    }, [loadMembers]);

    // Listen for termsAccepted event to load data after terms are accepted
    useEffect(() => {
        const handleTermsAccepted = () => {
            void loadMembers();
        };

        window.addEventListener('termsAccepted', handleTermsAccepted);
        return () => window.removeEventListener('termsAccepted', handleTermsAccepted);
    }, [loadMembers]);

    useEffect(() => {
        const handleProfilePictureUpdate = () => {
            void loadMembers({ silent: true });
        };

        window.addEventListener('profilePictureUpdated', handleProfilePictureUpdate);
        window.addEventListener('profileUpdated', handleProfilePictureUpdate);
        return () => {
            window.removeEventListener('profilePictureUpdated', handleProfilePictureUpdate);
            window.removeEventListener('profileUpdated', handleProfilePictureUpdate);
        };
    }, [loadMembers]);

    const handleMemberAdded = (newMember: Profile) => {
        void loadMembers({ silent: true });
    };

    const groupedMembers = useMemo(() => groupTeamMembersByRole(members), [members]);

    // Derive the visible tab so it stays valid without an extra render: use the
    // selected tab when it still exists, otherwise fall back to the first section.
    const currentTab = useMemo(() => {
        const ids = groupedMembers.sections.map((section) => section.id);
        if (activeTab && ids.includes(activeTab)) {
            return activeTab;
        }
        return ids[0] ?? null;
    }, [groupedMembers, activeTab]);

    return (
        <section className="team-grid page-container" aria-busy={loading}>
            {/* Title and Add Button Row */}
            <div className="page-bar">
                <div></div>
                <div></div>
                <div></div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    {showAddTeamMember && (
                        <AddTeamMemberButton
                            onClick={() => setShowAddModal(true)}
                            label="Add Team Member"
                        />
                    )}
                </div>
            </div>

            {teamName ? <div className="team-title" style={{ marginBottom: 10 }}>{teamName}</div> : null}

            {loading && <div>Loading…</div>}
            {!loading && error && <div className="form-alert is-error">{error}</div>}
            {!loading && !error && members.length === 0 && (
                <div className="form-alert">No team members yet.</div>
            )}

            {!loading && !error && members.length > 0 && (
                <>
                    {groupedMembers.sections.length > 0 && (
                        <>
                            <div className="team-role-tabs" role="tablist" aria-label="Team roles">
                                {groupedMembers.sections.map((section) => {
                                    const selected = currentTab === section.id;
                                    return (
                                        <button
                                            key={section.id}
                                            type="button"
                                            role="tab"
                                            id={`team-role-tab-${section.id}`}
                                            aria-selected={selected}
                                            aria-controls={`team-role-panel-${section.id}`}
                                            tabIndex={selected ? 0 : -1}
                                            className={`team-role-tab${selected ? " is-active" : ""}`}
                                            onClick={() => setActiveTab(section.id)}
                                        >
                                            {section.title}
                                        </button>
                                    );
                                })}
                            </div>
                            {groupedMembers.sections.map((section) => (
                                <section
                                    key={section.id}
                                    id={`team-role-panel-${section.id}`}
                                    role="tabpanel"
                                    aria-labelledby={`team-role-tab-${section.id}`}
                                    hidden={currentTab !== section.id}
                                    className="team-role-section"
                                >
                                    <TeamMemberCards members={section.members} />
                                </section>
                            ))}
                        </>
                    )}
                    {groupedMembers.unmatched.length > 0 && (
                        <TeamMemberCards members={groupedMembers.unmatched} />
                    )}
                </>
            )}

            {/* Add Team Member Modal */}
            <AddTeamMemberModal
                open={showAddModal}
                onClose={() => setShowAddModal(false)}
                onSaved={handleMemberAdded}
            />
        </section>
    );
}
